import { Box, Typography } from '@mui/material'
import { DataGrid, GridRowSelectionModel } from '@mui/x-data-grid'
import { FailedTransactionItem } from '@src/types/transaction-retry'
import { useTranslation } from 'react-i18next'
import { useTransactionDataGrid } from './TransactionRetry-hooks'

interface FailedTransactionsListProps {
  transactions: FailedTransactionItem[]
  selectedIds: Set<string>
  onSelectionChange: (ids: string[]) => void
  onViewReport: (fileName: string) => void
  onRetryProcessing: (fileName: string) => void
  onRetryMultiple: (fileNames: string[]) => void
  isLoading: boolean
}

const FailedTransactionsList = ({
  transactions,
  selectedIds,
  onSelectionChange,
  onViewReport,
  onRetryProcessing,
  onRetryMultiple: _onRetryMultiple,
  isLoading,
}: FailedTransactionsListProps) => {
  const { t } = useTranslation()
  const { columns, rows } = useTransactionDataGrid(transactions, onViewReport, onRetryProcessing)

  if (rows.length === 0 && !isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {t('admin.transactionRetry.labels.noFailedProcessFound')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ height: 600, width: '100%', overflow: 'auto' }}>
      {selectedIds.size > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('admin.transactionRetry.labels.selectedCount', { count: selectedIds.size })}
          </Typography>
        </Box>
      )}

      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        checkboxSelection
        rowSelectionModel={{
          type: 'include',
          ids: selectedIds,
        }}
        onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => {
          // GridRowSelectionModel contient un Set des IDs sélectionnés
          const selectedRowIds = Array.from(newSelection.ids) as string[]
          onSelectionChange(selectedRowIds)
        }}
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
          sorting: {
            sortModel: [{ field: 'processDate', sort: 'desc' }],
          },
        }}
        sx={{
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'action.hover',
          },
          '& .MuiDataGrid-main': {
            overflow: 'auto',
          },
          '& .MuiDataGrid-virtualScroller': {
            overflow: 'auto !important',
          },
        }}
      />
    </Box>
  )
}

export default FailedTransactionsList
